import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Paper } from '@mui/material';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { createManualMealPlanDraft, getClientById } from '@/lib/apiService';
import { ArrowLeft, CalendarDays, Loader2 } from 'lucide-react';
import SearchableClientDropdown from '@/components/shared/SearchableClientDropdown';
import { DietaryIntakeGoals, DietaryIntakeGoalsRef } from '@/clients/details/legacy/components/DietaryIntakeGoals';
import { MealTypesIntegrated, MealTypesIntegratedRef } from '@/components/clients/meal-settings/MealTypesIntegrated';
import { MEAL_PLAN_MAX_DAYS } from '@/components/meal-planning/MealPlanScheduleFields';
import { MealSuggestionsClientSummaryStrip } from '@/components/meal-planning/manual/MealSuggestionsClientSummaryStrip';
import { showErrorToast, showWarningToast } from '@/utils/toast-utils';
import {
  extractMealPlanDateConflicts,
  formatMealPlanDateConflictMessage,
} from '@/utils/meal-plan-date-conflicts';
import type { Client } from '@/types/client';
import type { GenerateMealPlanRequest } from '@/types/meal-plan';
import { formatDelimitedDisplayLabel, formatDisplayLabelList } from '@/utils/display-labels';
import { resolveClientTargetCalories } from '@/utils/client-goals';
import { validateMealPlanCalorieOverride } from '@/utils/meal-plan-calorie-override-validation';
import {
  trackManualMealPlanClientGoalsEditClicked,
  trackManualMealPlanClientGoalsSaved,
  trackManualMealPlanClientSelected,
  trackManualMealPlanCreateClicked,
  trackManualMealPlanDietaryPreferencesEditClicked,
  trackManualMealPlanDietaryPreferencesSaved,
  trackManualMealPlanExitClicked,
  trackManualMealPlanSetupCompleted,
  trackManualMealPlanSetupViewed,
  trackManualMealPlanSlotAdded,
  trackManualMealPlanSlotRemoved,
  type ManualMealPlanExitClickEvent,
} from '@/lib/analytics/trackers/manualMealPlanSetup';

const DAY_IN_MS = 1000 * 60 * 60 * 24;

function formatDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function addDaysToDateOnly(value: string, days: number): string {
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  date.setDate(date.getDate() + days);
  return formatDateInputValue(date);
}

function parseDateOnlyMillis(value: string): number | null {
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date.getTime();
}

function getInclusiveDateRangeDays(startDate: string, endDate: string): number | null {
  const startMs = parseDateOnlyMillis(startDate);
  const endMs = parseDateOnlyMillis(endDate);
  if (startMs == null || endMs == null) return null;
  return Math.floor((endMs - startMs) / DAY_IN_MS) + 1;
}

interface ManualMealPlanFormData {
  clientId: string;
  name: string;
  startDate: string;
  endDate: string;
}

type NormalizedDietaryGoals = {
  eerGoalCalories?: number;
  proteinGoalMin?: number;
  proteinGoalMax?: number;
  carbsGoalMin?: number;
  carbsGoalMax?: number;
  fatGoalMin?: number;
  fatGoalMax?: number;
  fiberGoalGrams?: number;
  waterGoalLiters?: number;
  allergies: string[];
  intolerances: string[];
  cuisineTypes: string[];
  preferences: string[];
};

type NumericGoalField =
  | 'eerGoalCalories'
  | 'proteinGoalMin'
  | 'proteinGoalMax'
  | 'carbsGoalMin'
  | 'carbsGoalMax'
  | 'fatGoalMin'
  | 'fatGoalMax'
  | 'fiberGoalGrams'
  | 'waterGoalLiters';

type DietaryPreferenceField = 'allergies' | 'intolerances' | 'cuisineTypes' | 'preferences';

const NUMERIC_GOAL_FIELDS: NumericGoalField[] = [
  'eerGoalCalories',
  'proteinGoalMin',
  'proteinGoalMax',
  'carbsGoalMin',
  'carbsGoalMax',
  'fatGoalMin',
  'fatGoalMax',
  'fiberGoalGrams',
  'waterGoalLiters',
];

const DIETARY_PREFERENCE_FIELDS: DietaryPreferenceField[] = [
  'allergies',
  'intolerances',
  'cuisineTypes',
  'preferences',
];

const ensureStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

const parseOptionalNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeDietaryGoalsInput = (input: unknown): NormalizedDietaryGoals => {
  const data = input && typeof input === 'object' ? (input as Record<string, unknown>) : {};

  return {
    eerGoalCalories: parseOptionalNumber(data.targetCalories ?? data.eerGoalCalories),
    proteinGoalMin: parseOptionalNumber(data.proteinMin ?? data.proteinGoalMin),
    proteinGoalMax: parseOptionalNumber(data.proteinMax ?? data.proteinGoalMax),
    carbsGoalMin: parseOptionalNumber(data.carbsMin ?? data.carbsGoalMin),
    carbsGoalMax: parseOptionalNumber(data.carbsMax ?? data.carbsGoalMax),
    fatGoalMin: parseOptionalNumber(data.fatMin ?? data.fatGoalMin),
    fatGoalMax: parseOptionalNumber(data.fatMax ?? data.fatGoalMax),
    fiberGoalGrams: parseOptionalNumber(data.fiberGrams ?? data.fiberGoalGrams ?? data.fibreGoalGrams),
    waterGoalLiters: parseOptionalNumber(data.waterLiters ?? data.waterGoalLiters),
    allergies: ensureStringArray(data.allergies),
    intolerances: ensureStringArray(data.foodIntolerances ?? data.intolerances),
    cuisineTypes: ensureStringArray(data.cuisineType ?? data.cuisinePreferences),
    preferences: ensureStringArray(data.preferences ?? data.dietaryPreferences),
  };
};

const normalizeClientBaselineGoals = (client: Client | null): NormalizedDietaryGoals => {
  const legacyClientGoal = (client as { clientGoal?: { eerGoalCalories?: unknown } } | null)?.clientGoal;

  return {
    eerGoalCalories: parseOptionalNumber(
      resolveClientTargetCalories(client) ??
      client?.eerOverride ??
      legacyClientGoal?.eerGoalCalories ??
      client?.eerCalories ??
      client?.eer
    ),
    proteinGoalMin: parseOptionalNumber(client?.clientGoals?.protein?.grams?.value),
    proteinGoalMax: parseOptionalNumber(client?.clientGoals?.protein?.grams?.value),
    carbsGoalMin: parseOptionalNumber(client?.clientGoals?.carbohydrates?.grams?.value),
    carbsGoalMax: parseOptionalNumber(client?.clientGoals?.carbohydrates?.grams?.value),
    fatGoalMin: parseOptionalNumber(client?.clientGoals?.totalFat?.grams?.value),
    fatGoalMax: parseOptionalNumber(client?.clientGoals?.totalFat?.grams?.value),
    fiberGoalGrams: parseOptionalNumber(client?.clientGoals?.dietaryFibre?.value),
    waterGoalLiters: parseOptionalNumber(client?.clientGoals?.water?.value),
    allergies: ensureStringArray(client?.allergies),
    intolerances: ensureStringArray(client?.foodIntolerances),
    cuisineTypes: ensureStringArray(client?.cuisineTypesPreferred),
    preferences: ensureStringArray(client?.dietaryPreferences),
  };
};

const areStringArraysEqual = (left: string[], right: string[]) => {
  if (left.length !== right.length) return false;

  const sortedLeft = [...left].sort();
  const sortedRight = [...right].sort();
  return sortedLeft.every((value, index) => value === sortedRight[index]);
};

const buildOverrideClientGoals = (
  current: NormalizedDietaryGoals,
  baseline: NormalizedDietaryGoals
): GenerateMealPlanRequest['overrideClientGoals'] | undefined => {
  const overrideClientGoals: GenerateMealPlanRequest['overrideClientGoals'] = {};

  NUMERIC_GOAL_FIELDS.forEach((field) => {
    const currentValue = current[field];
    const baselineValue = baseline[field];
    const resolvedValue = currentValue ?? baselineValue;
    if (resolvedValue !== undefined) {
      overrideClientGoals[field] = resolvedValue;
    }
  });

  if (current.allergies.length > 0) {
    overrideClientGoals.allergies = current.allergies;
  }
  if (current.intolerances.length > 0) {
    overrideClientGoals.intolerances = current.intolerances;
  }
  if (current.cuisineTypes.length > 0) {
    overrideClientGoals.cuisineTypes = current.cuisineTypes;
  }
  if (current.preferences.length > 0) {
    overrideClientGoals.preferences = current.preferences;
    overrideClientGoals.dietaryPreferences = current.preferences;
  }

  const hasAnyGoal = Object.keys(overrideClientGoals).length > 0;
  return hasAnyGoal ? overrideClientGoals : undefined;
};

const confirmCalorieOverrideForMealPlan = (client: Client, targetCalories: unknown): boolean => {
  const validation = validateMealPlanCalorieOverride(client, targetCalories);
  if (validation.status === 'ok') return true;

  if (validation.status === 'error') {
    showErrorToast(new Error(validation.message), 'Validation Error');
    return false;
  }

  return window.confirm(`${validation.message}\n\nDo you want to continue creating this meal plan?`);
};

const emptyDietaryGoals: NormalizedDietaryGoals = {
  allergies: [],
  intolerances: [],
  cuisineTypes: [],
  preferences: [],
};

const getCurrentDietaryGoals = (refValue: unknown, client: Client | null): NormalizedDietaryGoals => {
  const fromRef = normalizeDietaryGoalsInput(refValue);
  const hasAnyFromRef =
    Object.values(fromRef).some((value) => value !== undefined && !(Array.isArray(value) && value.length === 0));

  if (hasAnyFromRef) {
    return fromRef;
  }

  if (client) {
    const baseline = normalizeClientBaselineGoals(client);
    return {
      ...baseline,
      intolerances: baseline.intolerances,
    };
  }

  return emptyDietaryGoals;
};

type ManualMealPlanEntrySource =
  | 'client_details'
  | 'meal_planning_navigation'
  | 'meal_planning_client_view'
  | 'preselected_client_link'
  | 'direct_route';

const getManualMealPlanEntrySource = (
  fromPath: string,
  hasPreselectedClient: boolean
): ManualMealPlanEntrySource => {
  const normalizedFromPath = fromPath.trim().toLowerCase();

  if (normalizedFromPath.includes('/details-v2/')) return 'client_details';
  if (normalizedFromPath.startsWith('/meal-planning/client/')) return 'meal_planning_client_view';
  if (normalizedFromPath.startsWith('/meal-planning')) return 'meal_planning_navigation';
  if (hasPreselectedClient) return 'preselected_client_link';
  return 'direct_route';
};

const getChangedNumericGoalFields = (
  current: NormalizedDietaryGoals,
  baseline: NormalizedDietaryGoals
): string[] =>
  NUMERIC_GOAL_FIELDS.filter((field) => (current[field] ?? null) !== (baseline[field] ?? null));

const getChangedDietaryPreferenceFields = (
  current: NormalizedDietaryGoals,
  baseline: NormalizedDietaryGoals
): string[] =>
  DIETARY_PREFERENCE_FIELDS.filter((field) => !areStringArraysEqual(current[field], baseline[field]));

const CreateManualMealPlanPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preSelectedClientId = searchParams.get('clientId') || '';
  const fromPath = searchParams.get('from')?.trim() || '';
  const requestedPlanName = searchParams.get('name')?.trim() || '';
  const requestedDaysRaw = Number(searchParams.get('days'));
  const requestedDays =
    Number.isFinite(requestedDaysRaw) && requestedDaysRaw > 0
      ? Math.min(MEAL_PLAN_MAX_DAYS, Math.max(1, Math.floor(requestedDaysRaw)))
      : MEAL_PLAN_MAX_DAYS;

  const [isCreating, setIsCreating] = useState(false);
  const [isLoadingClient, setIsLoadingClient] = useState(!!preSelectedClientId);
  const [loadingClientDetails, setLoadingClientDetails] = useState(false);
  const [clientDetails, setClientDetails] = useState<Client | null>(null);
  const mealTypesRef = useRef<MealTypesIntegratedRef>(null);
  const dietaryGoalsRef = useRef<DietaryIntakeGoalsRef>(null);
  const setupViewedTrackedRef = useRef(false);
  const clientSelectedInSetupRef = useRef(false);
  const clientGoalsEditorOpenedRef = useRef(false);
  const dietaryPreferencesEditorOpenedRef = useRef(false);
  const mealSlotAddedInSetupRef = useRef(false);

  const manualMealPlanEntrySource = useMemo(
    () => getManualMealPlanEntrySource(fromPath, Boolean(preSelectedClientId)),
    [fromPath, preSelectedClientId]
  );

  const getManualMealPlanBaseProperties = useCallback(() => ({
    has_preselected_client: Boolean(preSelectedClientId),
    manual_meal_plan_entry_source: manualMealPlanEntrySource,
    meal_plan_flow: 'manual',
    meal_plan_surface: 'manual_setup',
    product_area: 'meal_plans',
    requested_days: requestedDays,
    requested_plan_name_provided: Boolean(requestedPlanName),
  }), [manualMealPlanEntrySource, preSelectedClientId, requestedDays, requestedPlanName]);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const defaultEndDate = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + (requestedDays - 1));
    return date.toISOString().slice(0, 10);
  }, [requestedDays]);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm<ManualMealPlanFormData>({
    defaultValues: {
      clientId: preSelectedClientId,
      name: requestedPlanName,
      startDate: today,
      endDate: defaultEndDate,
    },
    mode: 'onChange',
  });

  const clientId = watch('clientId');
  const startDateValue = watch('startDate');
  const endDateValue = watch('endDate');
  const maxSelectableEndDate = useMemo(() => {
    if (!startDateValue) return undefined;
    return addDaysToDateOnly(startDateValue, MEAL_PLAN_MAX_DAYS - 1);
  }, [startDateValue]);

  const selectedClientName = useMemo(() => {
    if (!clientDetails) return 'your client';
    const fullName = [clientDetails.firstName, clientDetails.lastName].filter(Boolean).join(' ').trim();
    return fullName || clientDetails.email || 'your client';
  }, [clientDetails]);

  const clientHighlights = useMemo(() => {
    if (!clientDetails) {
      return 'Select a client to load goals, dietary preferences, and meal slots.';
    }

    const highlights: string[] = [];
    const conditionText =
      typeof clientDetails.medicalConditions === 'string' ? clientDetails.medicalConditions.trim() : '';

    if (conditionText) highlights.push(formatDelimitedDisplayLabel(conditionText));
    if (clientDetails.dietaryPreferences?.length) {
      highlights.push(formatDisplayLabelList(clientDetails.dietaryPreferences.slice(0, 2)).join(' / '));
    }
    if (clientDetails.allergies?.length) {
      highlights.push(`No ${formatDisplayLabelList(clientDetails.allergies.slice(0, 2)).join(' / ')}`);
    }
    if (clientDetails.foodIntolerances?.length) {
      highlights.push(`${formatDisplayLabelList(clientDetails.foodIntolerances.slice(0, 2)).join(' / ')} sensitive`);
    }

    return highlights.length > 0 ? highlights.join(' · ') : 'Ready to configure calories, macros, and meal slots.';
  }, [clientDetails]);

  useEffect(() => {
    if (setupViewedTrackedRef.current) return;
    setupViewedTrackedRef.current = true;
    trackManualMealPlanSetupViewed(getManualMealPlanBaseProperties());
  }, [getManualMealPlanBaseProperties]);

  const clearDateConflictErrors = useCallback(() => {
    if (errors.startDate?.type === 'server') {
      clearErrors('startDate');
    }
    if (errors.endDate?.type === 'server') {
      clearErrors('endDate');
    }
  }, [clearErrors, errors.endDate?.type, errors.startDate?.type]);

  const handleClientSelected = useCallback(async (selectedClientId: string, client: Client) => {
    clearDateConflictErrors();

    if (!selectedClientId) {
      setClientDetails(null);
      return;
    }

    clientSelectedInSetupRef.current = true;
    trackManualMealPlanClientSelected({
      ...getManualMealPlanBaseProperties(),
      client_selected_in_setup: true,
    });

    setLoadingClientDetails(true);
    setClientDetails(null);

    try {
      const response = await getClientById(selectedClientId);
      if (response.client) {
        setClientDetails(response.client as Client);
      } else {
        setClientDetails(client);
      }
    } catch (error) {
      console.error('Error loading selected client details:', error);
      setClientDetails(client);
    } finally {
      setLoadingClientDetails(false);
    }
  }, [clearDateConflictErrors, getManualMealPlanBaseProperties]);

  const refreshClientData = useCallback(async () => {
    if (!clientId) return;

    try {
      const response = await getClientById(clientId, { force: true });
      if (response.client) {
        setClientDetails(response.client as Client);
      }
    } catch (error) {
      console.error('Error refreshing client details:', error);
    }
  }, [clientId]);

  useEffect(() => {
    if (!preSelectedClientId) {
      setIsLoadingClient(false);
      return;
    }

    const loadClient = async () => {
      setIsLoadingClient(true);
      setLoadingClientDetails(true);
      try {
        const response = await getClientById(preSelectedClientId);
        if (response.client) {
          setValue('clientId', preSelectedClientId, {
            shouldDirty: true,
            shouldValidate: true,
          });
          setClientDetails(response.client as Client);
        }
      } catch (error) {
        console.error('Error loading pre-selected client:', error);
        showErrorToast(error, 'Error Loading Client');
      } finally {
        setIsLoadingClient(false);
        setLoadingClientDetails(false);
      }
    };

    loadClient();
  }, [preSelectedClientId, setValue]);

  const handleClientGoalsEditOpen = useCallback(() => {
    clientGoalsEditorOpenedRef.current = true;
    trackManualMealPlanClientGoalsEditClicked({
      ...getManualMealPlanBaseProperties(),
      client_selected_in_setup: clientSelectedInSetupRef.current,
      has_current_client: Boolean(getValues('clientId')),
    });
  }, [getManualMealPlanBaseProperties, getValues]);

  const handleDietaryPreferencesEditOpen = useCallback(() => {
    dietaryPreferencesEditorOpenedRef.current = true;
    trackManualMealPlanDietaryPreferencesEditClicked({
      ...getManualMealPlanBaseProperties(),
      client_selected_in_setup: clientSelectedInSetupRef.current,
      has_current_client: Boolean(getValues('clientId')),
    });
  }, [getManualMealPlanBaseProperties, getValues]);

  const handleClientGoalsSaved = useCallback(() => {
    trackManualMealPlanClientGoalsSaved({
      ...getManualMealPlanBaseProperties(),
      client_selected_in_setup: clientSelectedInSetupRef.current,
      has_current_client: Boolean(getValues('clientId')),
    });
  }, [getManualMealPlanBaseProperties, getValues]);

  const handleDietaryPreferencesSaved = useCallback(() => {
    trackManualMealPlanDietaryPreferencesSaved({
      ...getManualMealPlanBaseProperties(),
      client_selected_in_setup: clientSelectedInSetupRef.current,
      has_current_client: Boolean(getValues('clientId')),
    });
  }, [getManualMealPlanBaseProperties, getValues]);

  const handleMealSlotAdded = useCallback(() => {
    mealSlotAddedInSetupRef.current = true;
    trackManualMealPlanSlotAdded({
      ...getManualMealPlanBaseProperties(),
      client_selected_in_setup: clientSelectedInSetupRef.current,
      has_current_client: Boolean(getValues('clientId')),
    });
  }, [getManualMealPlanBaseProperties, getValues]);

  const handleMealSlotRemoved = useCallback(() => {
    trackManualMealPlanSlotRemoved({
      ...getManualMealPlanBaseProperties(),
      client_selected_in_setup: clientSelectedInSetupRef.current,
      has_current_client: Boolean(getValues('clientId')),
    });
  }, [getManualMealPlanBaseProperties, getValues]);

  const handleBack = (eventName: ManualMealPlanExitClickEvent = 'Manual Meal Plan Back Clicked') => {
    trackManualMealPlanExitClicked(eventName, {
      ...getManualMealPlanBaseProperties(),
      client_selected_in_setup: clientSelectedInSetupRef.current,
      has_current_client: Boolean(getValues('clientId')),
    });

    if (fromPath) {
      navigate(fromPath);
      return;
    }
    navigate('/meal-planning');
  };

  const handleCreateMealPlan = async (data: ManualMealPlanFormData) => {
    const { clientId: formClientId, name: planName, startDate: formStart, endDate: formEnd } = data;
    trackManualMealPlanCreateClicked({
      ...getManualMealPlanBaseProperties(),
      client_selected_in_setup: clientSelectedInSetupRef.current,
      has_current_client: Boolean(formClientId),
    });

    if (!formClientId) {
      showErrorToast(new Error('Please select a client to create a meal plan.'), 'Client Required');
      return;
    }

    const duration = getInclusiveDateRangeDays(formStart, formEnd);
    if (duration == null || duration <= 0 || duration > MEAL_PLAN_MAX_DAYS) {
      showErrorToast(
        new Error(`Please select a start and end date within ${MEAL_PLAN_MAX_DAYS} days.`),
        'Invalid Date Range'
      );
      return;
    }

    const normalizedPlanName =
      planName?.trim() || `${selectedClientName !== 'your client' ? selectedClientName : 'Client'} Manual Meal Plan`;

    const mealTypes = mealTypesRef.current?.getCurrentMealTypes() || [];
    if (mealTypes.length > 0) {
      const hasMissingRequired = mealTypes.some(
        (meal) => !meal.name?.trim() || !meal.timeSlot?.trim() || !meal.mealType
      );
      if (hasMissingRequired) {
        showWarningToast(
          'Validation Error',
          'Please ensure all meal slots have a Name, Type, and Time filled out.'
        );
        return;
      }

      const mealsWithKcal = mealTypes.filter(
        (meal) => meal.targetKcal !== null && meal.targetKcal !== undefined && meal.targetKcal > 0
      );
      const mealsWithoutKcal = mealTypes.filter(
        (meal) => meal.targetKcal === null || meal.targetKcal === undefined || meal.targetKcal <= 0
      );

      if (mealsWithKcal.length > 0 && mealsWithoutKcal.length > 0) {
        showWarningToast(
          'Validation Error',
          'Either all meal slots should have target calories or none of them should. Please provide values for all meals or leave all empty.'
        );
        return;
      }
    }

    let overrideMealProgram: GenerateMealPlanRequest['overrideMealProgram'] | undefined;
    if (mealTypes.length > 0) {
      overrideMealProgram = {
        meals: mealTypes.map((meal, index) => ({
          mealOrder: index + 1,
          mealName: meal.name,
          mealTime: meal.timeSlot,
          mealType: meal.mealType,
          ...(meal.targetKcal !== null && meal.targetKcal !== undefined && meal.targetKcal > 0
            ? { targetCalories: meal.targetKcal }
            : {}),
        })),
      };
    }

    const dietaryGoals = getCurrentDietaryGoals(dietaryGoalsRef.current?.getCurrentValues(), clientDetails);
    if (!confirmCalorieOverrideForMealPlan(clientDetails, dietaryGoals.eerGoalCalories)) {
      return;
    }

    const baselineGoals = normalizeClientBaselineGoals(clientDetails);
    const overrideClientGoals = buildOverrideClientGoals(dietaryGoals, baselineGoals);
    const changedClientGoalFields = getChangedNumericGoalFields(dietaryGoals, baselineGoals);
    const changedDietaryPreferenceFields = getChangedDietaryPreferenceFields(dietaryGoals, baselineGoals);
    const clientGoalsChanged = changedClientGoalFields.length > 0;
    const dietaryPreferencesChanged = changedDietaryPreferenceFields.length > 0;
    const hasCustomMealSlots = mealTypes.length > 0;
    const mealPlanSetupChanged =
      clientGoalsChanged ||
      dietaryPreferencesChanged ||
      mealSlotAddedInSetupRef.current;
    const directClientSelectToCreate =
      clientSelectedInSetupRef.current &&
      !mealPlanSetupChanged;
    const directPreselectedClientToCreate =
      Boolean(preSelectedClientId) &&
      !clientSelectedInSetupRef.current &&
      !mealPlanSetupChanged;

    setIsCreating(true);
    try {
      const response = await createManualMealPlanDraft({
        clientId: formClientId,
        planDate: formStart,
        durationDays: duration,
        name: normalizedPlanName,
        allergies: dietaryGoals.allergies,
        intolerances: dietaryGoals.intolerances,
        cuisineTypes: dietaryGoals.cuisineTypes,
        preferences: dietaryGoals.preferences,
        dietaryPreferences: dietaryGoals.preferences,
        ...(overrideClientGoals ? { overrideClientGoals } : {}),
        ...(overrideMealProgram ? { overrideMealProgram } : {}),
      });

      const createdPlanId =
        (response as any)?.data?.mealPlanId ??
        (response as any)?.data?.planId ??
        (response as any)?.data?.draftId ??
        (response as any)?.mealPlanId ??
        (response as any)?.planId ??
        (response as any)?.draftId ??
        (response as any)?.id;

      if (!response || !createdPlanId) {
        throw new Error('Failed to get meal plan ID from response');
      }

      trackManualMealPlanSetupCompleted({
        ...getManualMealPlanBaseProperties(),
        client_goal_changed_fields: changedClientGoalFields,
        client_goals_changed: clientGoalsChanged,
        client_goals_editor_opened: clientGoalsEditorOpenedRef.current,
        client_selected_in_setup: clientSelectedInSetupRef.current,
        dietary_preference_changed_fields: changedDietaryPreferenceFields,
        dietary_preferences_changed: dietaryPreferencesChanged,
        dietary_preferences_editor_opened: dietaryPreferencesEditorOpenedRef.current,
        direct_client_select_to_create: directClientSelectToCreate,
        direct_preselected_client_to_create: directPreselectedClientToCreate,
        from_client_details_page: manualMealPlanEntrySource === 'client_details',
        from_meal_planning_navigation: manualMealPlanEntrySource === 'meal_planning_navigation',
        has_custom_meal_slots: hasCustomMealSlots,
        has_override_client_goals: Boolean(overrideClientGoals),
        has_override_meal_program: Boolean(overrideMealProgram),
        meal_plan_setup_changed: mealPlanSetupChanged,
        meal_slot_count: mealTypes.length,
        meal_slots_added_in_setup: mealSlotAddedInSetupRef.current,
        plan_duration_days: duration,
        plan_name_provided: Boolean(planName?.trim()),
      });

      const nutritionPath = `/meal-plans/${createdPlanId}/clients/${formClientId}/nutrition/1`;
      const withFrom = fromPath ? `${nutritionPath}?from=${encodeURIComponent(fromPath)}` : nutritionPath;
      navigate(withFrom, {
        state: {
          planName: normalizedPlanName,
          from: fromPath || undefined,
          preferredChoices: {
            allergies: dietaryGoals.allergies,
            intolerances: dietaryGoals.intolerances,
            cuisineTypes: dietaryGoals.cuisineTypes,
            preferences: dietaryGoals.preferences,
          },
        },
      });
    } catch (error) {
      console.error('Error creating meal plan:', error);
      const dateConflicts = extractMealPlanDateConflicts(error);
      if (dateConflicts.length > 0) {
        const message = formatMealPlanDateConflictMessage(dateConflicts);
        setError('startDate', { type: 'server', message });
        setError('endDate', { type: 'server', message });
        showWarningToast('Date conflict', message);
        return;
      }
      showErrorToast(error, 'Error');
    } finally {
      setIsCreating(false);
    }
  };

  const showCreateDisabled =
    !clientId ||
    isCreating ||
    isLoadingClient ||
    loadingClientDetails ||
    !!errors.clientId ||
    !!errors.startDate ||
    !!errors.endDate;

  return (
    <Box data-demo-id="manual-plan-page" sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid rgba(15, 23, 42, 0.08)',
          bgcolor: '#fff',
        }}
      >
        {isLoadingClient ? (
          <Card className="m-4 sm:m-5 lg:m-7">
            <CardContent className="flex items-center justify-center py-16">
              <Loader2 className="mr-3 h-8 w-8 animate-spin text-primary" />
              <span className="text-lg text-muted-foreground">Loading client data...</span>
            </CardContent>
          </Card>
        ) : (
          <form
            onSubmit={handleSubmit(handleCreateMealPlan)}
            className="space-y-6 p-4 sm:p-5 lg:px-7 lg:pt-6 lg:pb-7"
          >
            <section className="border-b border-slate-200 pb-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex min-w-0 flex-col gap-2 md:flex-row md:items-center">
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-auto w-fit gap-1 px-0 text-base font-medium text-slate-600 hover:bg-transparent hover:text-slate-800"
                    onClick={() => handleBack('Manual Meal Plan Back Clicked')}
                    data-analytics-ignore="true"
                  >
                    <ArrowLeft className="h-3 w-3" />
                    Go Back
                  </Button>
                  <span className="hidden h-6 w-px bg-slate-300 md:block" />
                  <div className="md:pl-2">
                    <h1 className="text-lg font-semibold text-slate-900">Manual Meal Plan</h1>
                    <p className="text-xs text-slate-500">Create personalized meal plans</p>
                  </div>
                </div>

              </div>
            </section>

            {clientDetails && clientId ? (
              <div data-demo-id="manual-plan-client-summary">
                <MealSuggestionsClientSummaryStrip client={clientDetails} clientId={clientId} />
              </div>
            ) : null}

            <section data-demo-id="manual-plan-client" className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="space-y-2">
                <h2 className="text-base font-semibold text-slate-800">Select Client</h2>
                <Controller
                  name="clientId"
                  control={control}
                  rules={{ required: 'Please select a client' }}
                  render={({ field }) => (
                    <SearchableClientDropdown
                      value={field.value}
                      onChange={(selectedClientId, selectedClient) => {
                        field.onChange(selectedClientId);
                        handleClientSelected(selectedClientId, selectedClient);
                      }}
                      placeholder="Search and select a client..."
                      error={errors.clientId?.message as string | undefined}
                    />
                  )}
                />
                {loadingClientDetails && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading client details...
                  </div>
                )}
              </div>
            </section>

            <section data-demo-id="manual-plan-dates" className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="space-y-4">
                <h2 className="flex items-center gap-2 text-base font-semibold text-slate-800">
                  <CalendarDays className="h-4 w-4 text-cyan-600" />
                  Plan Date Range
                </h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <Controller
                    name="startDate"
                    control={control}
                    rules={{
                      required: 'Please select a start date',
                      validate: (value) => {
                        const selectedDate = new Date(value);
                        if (isNaN(selectedDate.getTime())) return 'Please enter a valid date';
                        const todayDate = new Date(today);
                        todayDate.setHours(0, 0, 0, 0);
                        if (selectedDate < todayDate) return 'Start date cannot be in the past';
                        return true;
                      },
                    }}
                    render={({ field }) => (
                      <div className="space-y-2">
                        <Label htmlFor="manual-plan-start-date">Start Date</Label>
                        <input
                          id="manual-plan-start-date"
                          type="date"
                          min={today}
                          value={field.value}
                          onChange={(event) => {
                            const nextStartDate = event.target.value;
                            if (!nextStartDate) {
                              field.onChange(nextStartDate);
                              return;
                            }

                            const durationDays = getInclusiveDateRangeDays(startDateValue, endDateValue);
                            field.onChange(nextStartDate);
                            if (durationDays != null) {
                              setValue('endDate', addDaysToDateOnly(nextStartDate, durationDays - 1), {
                                shouldDirty: true,
                                shouldValidate: true,
                              });
                            }
                          }}
                          className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        />
                        {errors.startDate && (
                          <p className="text-sm text-red-500">{errors.startDate.message}</p>
                        )}
                      </div>
                    )}
                  />

                  <Controller
                    name="endDate"
                    control={control}
                    rules={{
                      required: 'Please select an end date',
                      validate: (value) => {
                        const endMs = parseDateOnlyMillis(value);
                        const todayMs = parseDateOnlyMillis(today);
                        if (endMs == null || todayMs == null) return 'Please enter a valid date';

                        if (endMs < todayMs) return 'End date cannot be in the past';
                        const currentStartDate = getValues('startDate');
                        if (!currentStartDate) return 'Please select a start date first';

                        const startMs = parseDateOnlyMillis(currentStartDate);
                        if (startMs == null) return 'Please select a start date first';
                        if (endMs < startMs) return 'End date cannot be before start date';

                        const durationDays = Math.floor((endMs - startMs) / DAY_IN_MS) + 1;
                        if (durationDays > MEAL_PLAN_MAX_DAYS) {
                          return `Meal plans cannot exceed ${MEAL_PLAN_MAX_DAYS} days`;
                        }
                        return true;
                      },
                    }}
                    render={({ field }) => (
                      <div className="space-y-2">
                        <Label htmlFor="manual-plan-end-date">End Date</Label>
                        <input
                          id="manual-plan-end-date"
                          type="date"
                          min={startDateValue || today}
                          max={maxSelectableEndDate}
                          value={field.value}
                          onChange={(event) => field.onChange(event.target.value)}
                          className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        />
                        {errors.endDate && (
                          <p className="text-sm text-red-500">{errors.endDate.message}</p>
                        )}
                      </div>
                    )}
                  />

                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <div className="space-y-2">
                        <Label htmlFor="manual-plan-name">Plan Name (Optional)</Label>
                        <Input
                          id="manual-plan-name"
                          placeholder="e.g., 8-Week Metabolic Reset"
                          value={field.value}
                          onChange={field.onChange}
                        />
                      </div>
                    )}
                  />
                </div>
              </div>
            </section>

            {clientId && (
              <>
                {loadingClientDetails ? (
                  <Card className="border border-border/40">
                    <CardContent className="flex items-center justify-center py-10">
                      <Loader2 className="mr-2 h-5 w-5 animate-spin text-primary" />
                      <span className="text-sm text-muted-foreground">Loading client goals...</span>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    <div data-demo-id="manual-plan-goals">
                      <DietaryIntakeGoals
                        ref={dietaryGoalsRef}
                        clientId={clientId}
                        client={clientDetails || undefined}
                        refreshClientData={refreshClientData}
                        hideSaveButtons={true}
                        compactView={true}
                        onGoalsEditOpen={handleClientGoalsEditOpen}
                        onPreferencesEditOpen={handleDietaryPreferencesEditOpen}
                        onGoalsSaved={handleClientGoalsSaved}
                        onPreferencesSaved={handleDietaryPreferencesSaved}
                      />
                    </div>
                    <div data-demo-id="manual-plan-meal-slots">
                      <MealTypesIntegrated
                        ref={mealTypesRef}
                        clientId={clientId}
                        client={clientDetails || undefined}
                        hideSaveButtons={true}
                        compactView={true}
                        onMealSlotAdded={handleMealSlotAdded}
                        onMealSlotRemoved={handleMealSlotRemoved}
                      />
                    </div>
                  </>
                )}
              </>
            )}

            <section className="flex items-center justify-between">
              <Button
                type="button"
                variant="ghost"
                className="text-slate-600"
                onClick={() => handleBack('Manual Meal Plan Cancel Clicked')}
                data-analytics-ignore="true"
              >
                Cancel
              </Button>

              <Button
                type="submit"
                data-demo-id="manual-plan-create"
                className="rounded-full bg-cyan-500 px-6 text-white hover:bg-cyan-600"
                disabled={showCreateDisabled}
                data-analytics-ignore="true"
              >
                {isCreating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Meal Plan'
                )}
              </Button>
            </section>
          </form>
        )}
      </Paper>
    </Box>
  );
};

export default CreateManualMealPlanPage;
